import { useState } from 'react';

import { Link } from 'react-router-dom';

import { DeleteMatterDialog } from './delete-matter-dialog';
import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { useDeleteMatter } from '@/api/mutations/useDeleteMatter';
import { Matter, Case } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import isPlural from '@/helpers/isPlural';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Input } from '@/new/components/shadcn/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface MatterCardProps {
  matter: Matter;
  isDraft?: boolean;
  matchedCases?: Array<Pick<Case, 'id' | 'name'>>;
  searchFilter?: string;
}

export function MatterCard({ matter, isDraft = false, matchedCases = [], searchFilter }: MatterCardProps) {
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const hasMatches = matchedCases.length > 0;
  const matterLink = isDraft ? `/matters/new/${matter.id}` : `/matters/${matter.id}`;

  // Check if user can delete this matter
  const isCreator = matter.createdById === user?.id;
  const hasCaseDeleteAnyPermission = hasPermission(Permission.CaseDeleteAny);
  const canDelete = isCreator || hasCaseDeleteAnyPermission;

  const cardClasses = isDraft
    ? 'group animate-in fade-in flex w-full flex-col rounded-md border border-slate-200 bg-slate-50/50 shadow-lg/2 duration-150 hover:border-slate-300 hover:bg-slate-50 hover:shadow-lg/10 hover:shadow-slate-400'
    : 'group animate-in fade-in flex w-full flex-col rounded-md border border-gray-200 shadow-lg/2 duration-150 hover:shadow-lg/10 hover:shadow-teal-500';
  const headerBgClasses = isDraft ? 'bg-slate-50/70 group-hover:bg-slate-100/80' : 'bg-gray-50';
  const iconClasses = isDraft
    ? 'lucide--file-pen text-slate-600 duration-150 group-hover:text-slate-800'
    : 'lucide--book text-gray-500 duration-150 group-hover:text-teal-500';
  const borderTopClasses = isDraft ? 'border-slate-200' : 'border-gray-200';

  return (
    <Link to={matterLink} className={cardClasses}>
      <div className={`flex items-center justify-between gap-3 rounded-t-md ${headerBgClasses} p-3 px-4.5`}>
        <div className="flex min-w-0 flex-1 items-center gap-3 text-base font-medium text-gray-700 duration-150 hover:text-gray-900">
          <Icon className={iconClasses} />
          <span className="truncate">{matter.name || (isDraft ? 'Untitled Matter' : '')}</span>

          {isDraft && (
            <span className="flex-shrink-0 rounded-md border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700 group-hover:border-slate-400 group-hover:bg-slate-200 group-hover:text-slate-800">
              Draft
            </span>
          )}

          {matter.code && (
            <span className="flex-shrink-0 rounded-md border border-gray-300 bg-white p-0.5 px-1 pb-0.25 text-xs text-gray-500">
              {matter.code}
            </span>
          )}
        </div>

        {!isDraft && searchFilter && hasMatches && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="flex flex-shrink-0 items-center gap-1 rounded-full border border-dashed border-blue-300 bg-blue-50 px-1.5 py-0 transition-all duration-200 hover:border-blue-400 hover:bg-blue-100 hover:shadow-sm"
              >
                <Icon className="lucide--search h-2.5 w-2.5 text-blue-600" />
                <span className="text-[11px] font-medium text-blue-700">
                  {matchedCases.length} search {matchedCases.length === 1 ? 'match' : 'matches'}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0 shadow-xl" side="bottom" align="end">
              <div className="rounded-lg border-0 bg-white">
                <div className="rounded-t bg-blue-50 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
                      <Icon className="lucide--search h-3 w-3 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-gray-900">Matched Subsets</h4>
                      <p className="text-[10px] text-gray-600">
                        {matchedCases.length} subset{matchedCases.length === 1 ? '' : 's'} found
                      </p>
                    </div>
                  </div>
                </div>

                <div className="max-h-48 overflow-y-auto p-1.5">
                  <div className="flex flex-col gap-0.5">
                    {matchedCases.map((matchedCase) => (
                      <Link
                        key={matchedCase.id}
                        to={`/matters/${matter.id}/subsets/${matchedCase.id}`}
                        className="group/case flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-all duration-200 hover:bg-blue-50 hover:shadow-sm"
                      >
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 transition-colors duration-200 group-hover/case:bg-blue-100">
                          <Icon className="lucide--file h-2.5 w-2.5 text-gray-600 group-hover/case:text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium text-gray-900 group-hover/case:text-blue-900">
                            {matchedCase.name}
                          </p>
                        </div>
                        <Icon className="lucide--external-link h-3 w-3 text-gray-400 opacity-0 transition-all duration-200 group-hover/case:text-blue-500 group-hover/case:opacity-100" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.preventDefault()}
              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-all duration-200 hover:bg-gray-200 hover:text-gray-600"
            >
              <Icon className="lucide--ellipsis-vertical h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canDelete ? (
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  setDeleteDialogOpen(true);
                }}
                className="text-red-600 focus:bg-red-50 focus:text-red-700"
              >
                <Icon className="lucide--trash-2 h-4 w-4" />
                Delete matter
              </DropdownMenuItem>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <DropdownMenuItem
                      disabled
                      className="cursor-not-allowed text-gray-400"
                      onClick={(e) => e.preventDefault()}
                    >
                      <Icon className="lucide--trash-2 h-4 w-4" />
                      Delete matter
                    </DropdownMenuItem>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-56" sideOffset={16}>
                  <p className="text-center">
                    Only the matter creator or users with admin permissions can delete this matter
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex grow flex-col">
        <div className={`flex grow flex-col rounded-b-md border-t ${borderTopClasses} p-4 px-5 text-gray-700`}>
          <p className="line-clamp-2">{matter.description || (isDraft ? 'No description yet' : '')}</p>

          <div className="flex items-end justify-between">
            <div>
              <p className="mb-0.5 flex items-center gap-2">
                <Icon className="lucide--group text-gray-500" />
                <span className="font-medium">
                  {matter.casesCount ?? 0}{' '}
                  <span className="font-normal">
                    {isPlural({ one: 'subset', more: 'subsets', number: matter.casesCount ?? 0 })}
                  </span>
                </span>
              </p>

              <p className="flex items-center gap-2">
                <Icon className="lucide--file-text text-gray-500" />
                <span className="font-medium">
                  {matter.docsCount ?? 0}{' '}
                  <span className="font-normal">
                    {isPlural({ one: 'doc', more: 'docs', number: matter.docsCount ?? 0 })}
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <DeleteMatterDialog
        matterId={matter.id}
        matterName={matter.name}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      />
    </Link>
  );
}
